/**
 * Community — channels, live chat and shared bets, in Floodlight (v1.71:
 * the original black ground and glass panels, purple/blue accents, Floodlight
 * type).
 *
 * Layout by width:
 *   ≥ 1180  channels | conversation | members
 *   ≥ 760   channels | conversation, members in a slide-over
 *   < 760   conversation only; channels and members open as slide-overs
 * Columns sit in one rounded glass panel split by hairlines. Members come
 * from member cards (communities/main/members, written by the communityJoin
 * function), never from the users collection. Signed-out visitors get the
 * landing and a glass sign-in stage instead of an input that fails.
 */
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { View, Text, StyleSheet, Platform, useWindowDimensions, Modal, Pressable } from "react-native";
import { Ionicons } from "@expo/vector-icons";

import { useAuth } from "../context/AuthContext";
import { useNavigation } from "../context/NavigationContext";
import ChannelsSidebar from "../components/community/ChannelsSidebar";
import MembersList from "../components/community/MembersList";
import ChannelContent from "../components/community/ChannelContent";
import Appear from "../components/motion/Appear";
import Reveal from "../components/motion/Reveal";
import { FLPage, FLButton, FLHeadline, FLMarquee, FLKicker, FL, FONT, label, body, nums, gutter, glass } from "../components/floodlight";
import { IS_WEB } from "../utils/motion";
import injectCss from "../utils/injectCss";

import {
  subscribeToChannels,
  subscribeToMemberPresence,
  initPresence,
  joinCommunityServer,
} from "../services/communityService";

const COMMUNITY_ID = "main";

// What members get, shown to signed-out visitors (real features only).
const PERKS = [
  ["chatbubbles-outline", "Channels by sport", "Talk lines and news in rooms organized by sport and league."],
  ["share-social-outline", "Post from your slip", "Share a bet straight from your bet slip, priced and ready to read."],
  ["pulse-outline", "Today's price in one tap", "Re-run any shared bet in one tap to see what the number is now."],
];

const BAND = ["Talk lines", "Share bets", "Compare notes", "Re-run any bet"];

// Community-only additions to the Floodlight kit: the online dot breathes
// (green: it means "live"), and a message brightens on a white wash with a
// light-purple gutter under the pointer.
const COMMUNITY_CSS = `
[data-fl-online] { animation: flOnline 2.4s ease-in-out infinite; }
@keyframes flOnline { 0%, 100% { box-shadow: 0 0 0 0 rgba(74,222,128,0.6); } 50% { box-shadow: 0 0 0 5px rgba(74,222,128,0); } }
[data-msg] { border-radius: 12px; transition: background-color 200ms, box-shadow 200ms; }
@media (hover: hover) and (pointer: fine) {
  [data-msg]:hover { background-color: var(--fl-ink-wash); box-shadow: inset 2px 0 0 var(--fl-green-ink); }
}
@media (prefers-reduced-motion: reduce) { [data-fl-online] { animation: none; } }
`;

function findChannel(categories, id) {
  for (const cat of categories) {
    for (const ch of cat.channels ?? []) {
      if (ch.id === id) return { channel: ch, categoryId: cat.id };
      for (const sub of ch.subChannels ?? []) if (sub.id === id) return { channel: sub, categoryId: cat.id };
    }
  }
  return { channel: null, categoryId: null };
}

function SlideOver({ open, side = "left", onClose, children, label: name }) {
  if (!open) return null;
  return (
    <Modal visible transparent animationType="fade" onRequestClose={onClose}>
      <View style={[styles.overlay, { flexDirection: side === "left" ? "row" : "row-reverse" }]}>
        <Appear from={side === "left" ? "left" : "right"} distance={24} style={[styles.sheet, side === "left" ? styles.sheetLeft : styles.sheetRight]} accessibilityLabel={name}>
          {children}
        </Appear>
        <Pressable style={{ flex: 1 }} onPress={onClose} accessibilityRole="button" accessibilityLabel="Close panel" />
      </View>
    </Modal>
  );
}

export default function CommunityScreen() {
  if (IS_WEB) injectCss("community-floodlight", COMMUNITY_CSS);
  const { user } = useAuth();
  const { navigateTo } = useNavigation();
  const { width } = useWindowDimensions();
  const threeCol = width >= 1180;
  const twoCol = width >= 760;
  const pad = gutter(width);

  const [categories, setCategories] = useState([]);
  const [channelsLoading, setChannelsLoading] = useState(true);
  const [channelsError, setChannelsError] = useState(null);
  const [members, setMembers] = useState([]);
  const [activeChannelId, setActiveChannelId] = useState(null);
  const [channelsOpen, setChannelsOpen] = useState(false);
  const [membersOpen, setMembersOpen] = useState(false);

  useEffect(() => {
    if (activeChannelId || !categories.length) return;
    const first = categories[0]?.channels?.[0];
    if (first) setActiveChannelId(first.id);
  }, [categories, activeChannelId]);

  const { channel: activeChannel, categoryId: activeCategoryId } = useMemo(() => findChannel(categories, activeChannelId), [categories, activeChannelId]);
  const activeChannelName = activeChannel ? (activeChannel.displayName ?? activeChannel.name ?? activeChannel.id) : null;

  const displayMembers = useMemo(
    () => members.map((m) => (m.uid === user?.uid ? { ...m, onlineStatus: "online" } : m)),
    [members, user?.uid],
  );
  const onlineCount = displayMembers.filter((m) => m.onlineStatus === "online").length;

  useEffect(() => {
    if (!user?.uid) { setChannelsLoading(false); return undefined; }
    setChannelsLoading(true);
    const unsubChannels = subscribeToChannels(COMMUNITY_ID, (cats) => { setCategories(cats); setChannelsLoading(false); }, (e) => setChannelsError(e));
    const unsubMembers = subscribeToMemberPresence(COMMUNITY_ID, setMembers);
    return () => { unsubChannels(); unsubMembers(); };
  }, [user?.uid]);

  useEffect(() => {
    if (!user?.uid) return undefined;
    let cleanup = () => {};
    let gone = false;
    // Member card first (the presence mirror only updates existing cards).
    joinCommunityServer(COMMUNITY_ID).finally(() => {
      if (gone) return;
      initPresence(COMMUNITY_ID, user.uid).then(({ cleanup: fn }) => { if (gone) fn(); else cleanup = fn; });
    });
    return () => { gone = true; cleanup(); };
  }, [user?.uid]);

  const selectChannel = useCallback((id) => { setActiveChannelId(id); setChannelsOpen(false); }, []);
  const signIn = useCallback(() => navigateTo("Login", { redirectAfterLogin: "Community" }), [navigateTo]);
  const register = useCallback(() => navigateTo("Register"), [navigateTo]);

  if (!user) return <SignedOut width={width} pad={pad} onSignIn={signIn} onRegister={register} />;

  const channels = (
    <ChannelsSidebar
      categories={categories}
      loading={channelsLoading}
      error={channelsError}
      selectedChannelId={activeChannelId}
      onChannelSelect={selectChannel}
      style={!twoCol ? { width: "100%", height: "100%", borderRightWidth: 0 } : undefined}
    />
  );
  const memberList = <MembersList members={displayMembers} style={!threeCol ? { width: "100%", height: "100%", borderLeftWidth: 0 } : { width: 232 }} />;
  const headSize = width >= 1100 ? 56 : width >= 600 ? 44 : 30;

  return (
    <View style={styles.viewport}>
      <FLPage scroll={false} contentStyle={{ paddingHorizontal: pad, paddingBottom: width >= 600 ? 20 : 12 }}>
        <View style={[styles.bar, { paddingTop: width >= 768 ? 22 : 12, paddingBottom: width >= 600 ? 16 : 10 }]}>
          <View style={{ flex: 1, minWidth: 0 }}>
            {width >= 600 && <FLKicker style={{ marginBottom: 10 }}>BetOptimized</FLKicker>}
            <FLHeadline lines={["Community"]} size={headSize} />
          </View>
          {width >= 900 && (
            <Text style={[body(14), styles.barSub]} numberOfLines={2}>Talk lines, share bets, compare notes. Re-run any shared bet for today's price.</Text>
          )}
          <View style={styles.onlineTag} accessible accessibilityLabel={`${onlineCount} members online`}>
            <View style={styles.onlineDot} {...(IS_WEB ? { dataSet: { flOnline: "" } } : {})} />
            <Text style={[styles.onlineText, nums]}>{onlineCount} online</Text>
          </View>
        </View>

        <View style={styles.columns}>
          {twoCol && channels}
          <ChannelContent
            communityId={COMMUNITY_ID}
            categoryId={activeCategoryId}
            channelId={activeChannelId}
            channelName={activeChannelName}
            channelTopic={activeChannel?.description || activeChannel?.topic || null}
            compact={!twoCol}
            onOpenChannels={!twoCol ? () => setChannelsOpen(true) : undefined}
            onOpenMembers={!threeCol ? () => setMembersOpen(true) : undefined}
            onlineCount={onlineCount}
          />
          {threeCol && memberList}
        </View>
      </FLPage>

      <SlideOver open={channelsOpen && !twoCol} side="left" onClose={() => setChannelsOpen(false)} label="Channels">{channels}</SlideOver>
      <SlideOver open={membersOpen && !threeCol} side="right" onClose={() => setMembersOpen(false)} label="Members">{memberList}</SlideOver>
    </View>
  );
}

/** Signed out: the room on the glass look, then a glass stage with the way in. */
function SignedOut({ width, pad, onSignIn, onRegister }) {
  const wide = width >= 960;
  const phone = width < 600;
  // Fit "WHERE THE EDGE" (the longest line, ~9.5em in Floodlight caps at width 104) to its column.
  const heroCol = wide ? ((Math.min(width, 1480) - pad * 2 - 56) * 1.5) / 2.5 : width - pad * 2;
  const heroSize = Math.max(30, Math.min(88, Math.floor(heroCol / 9.8)));
  // "THE ROOM." is ~6.4em wide; the stage has 24px of inner padding on phones.
  const gateSize = wide ? 84 : Math.min(48, Math.floor((width - pad * 2 - 48) / 6.6));
  const cta = [styles.cta, phone && styles.ctaPhone];

  return (
    <FLPage bottom={0}>
      <View style={[styles.hero, { paddingHorizontal: pad }, wide && styles.heroWide]}>
        <View style={{ flex: wide ? 1.5 : undefined }}>
          <FLKicker index={1}>Community</FLKicker>
          <FLHeadline lines={["The room", "where the edge", "gets shared"]} accentLine={2} size={heroSize} style={{ marginTop: 22 }} />
          <Text style={[body(phone ? 17 : 19), styles.lede]}>
            Talk lines, share bets, compare notes. Re-run any shared bet for today's price.
          </Text>
          <View style={styles.ctaRow}>
            <FLButton label="Sign in" variant="green" onPress={onSignIn} size="lg" style={cta} />
            <FLButton label="Create a free account" variant="outline" onPress={onRegister} size="lg" style={cta} />
          </View>
        </View>

        <View style={[styles.perks, wide ? { flex: 1, marginTop: 64, alignSelf: "flex-start" } : { marginTop: 48 }]} accessibilityRole="list">
          <Text style={[label(11), styles.perksHead]}>Inside the room</Text>
          {PERKS.map(([icon, title, text], i) => (
            <Reveal key={title} index={i} distance={16}>
              <View style={[styles.perkRow, i === PERKS.length - 1 && { borderBottomWidth: 0 }]} dataSet={{ flRow: "static" }} accessibilityRole="listitem">
                <Text style={styles.perkNum} dataSet={{ flRowNum: "" }} aria-hidden>{String(i + 1).padStart(2, "0")}</Text>
                <View style={{ flex: 1, gap: 4 }} dataSet={{ flRowBody: "" }}>
                  <Text style={styles.perkTitle}>{title}</Text>
                  <Text style={body(14)} dataSet={{ flLit: "" }}>{text}</Text>
                </View>
                <Ionicons name={icon} size={22} color={FL.greenInk} accessibilityElementsHidden />
              </View>
            </Reveal>
          ))}
        </View>
      </View>

      <FLMarquee items={BAND} size={wide ? 40 : 26} duration={40} style={{ marginTop: wide ? 72 : 48 }} />

      <View style={[styles.gate, { paddingHorizontal: pad, marginTop: wide ? 88 : 56 }]}>
        <Reveal style={[styles.gateInner, { paddingHorizontal: wide ? 56 : 24, paddingTop: wide ? 64 : 48, paddingBottom: wide ? 72 : 56 }]}>
          <FLKicker index={2} tone="paper">Members</FLKicker>
          <FLHeadline lines={["Join", "the room."]} accentLine={1} tone="paper" size={gateSize} level={2} enter={false} style={{ marginTop: 22 }} />
          <Text style={[body(phone ? 16 : 18), styles.gateBody]}>
            Sign in to read the channels, post a bet from your slip, and see what other members are finding.
          </Text>
          <View style={styles.ctaRow}>
            <FLButton label="Sign in" variant="green" onPress={onSignIn} size="lg" style={cta} />
            <FLButton label="Create a free account" variant="paper" onPress={onRegister} size="lg" style={cta} />
          </View>
        </Reveal>
      </View>
    </FLPage>
  );
}

const WEB = Platform.OS === "web";

const styles = StyleSheet.create({
  viewport: {
    flex: 1,
    backgroundColor: FL.paper,
    height: WEB ? "100vh" : "100%",
    maxHeight: WEB ? "100vh" : "100%",
    overflow: "hidden",
  },
  bar: { flexDirection: "row", alignItems: "flex-end", gap: 20 },
  barSub: { maxWidth: 360, color: FL.muted, paddingBottom: 4 },
  onlineTag: { ...glass({ radius: 999 }), flexDirection: "row", alignItems: "center", gap: 8, paddingHorizontal: 14, height: 36, marginBottom: 2 },
  onlineDot: { width: 8, height: 8, borderRadius: 999, backgroundColor: FL.pos },
  onlineText: { fontFamily: FONT, fontSize: 12, fontWeight: "700", letterSpacing: 1, textTransform: "uppercase", color: FL.ink },
  columns: { ...glass({ radius: 22 }), flex: 1, flexDirection: "row", overflow: "hidden" },

  overlay: { flex: 1, backgroundColor: "rgba(0,0,0,0.55)", ...(WEB ? { backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)" } : {}) },
  sheet: {
    width: "86%", maxWidth: 320, height: "100%", backgroundColor: FL.paper, borderColor: FL.hair2,
    ...(WEB ? { backgroundImage: "linear-gradient(rgba(255,255,255,0.06), rgba(255,255,255,0.06))", boxShadow: "0 0 60px rgba(0,0,0,0.6)" } : {}),
  },
  sheetLeft: { borderRightWidth: 1, borderTopRightRadius: 22, borderBottomRightRadius: 22, overflow: "hidden" },
  sheetRight: { borderLeftWidth: 1, borderTopLeftRadius: 22, borderBottomLeftRadius: 22, overflow: "hidden" },

  hero: { paddingTop: 48, alignSelf: "center", width: "100%", maxWidth: 1480 },
  heroWide: { flexDirection: "row", gap: 56, paddingTop: 72 },
  lede: { marginTop: 28, maxWidth: 600, color: FL.ink2 },
  ctaRow: { flexDirection: "row", flexWrap: "wrap", gap: 12, marginTop: 32 },
  cta: { minWidth: 220 },
  ctaPhone: { minWidth: 0, width: "100%" },

  perks: { ...glass({ radius: 22 }), alignSelf: "stretch", paddingVertical: 8, overflow: "hidden" },
  perksHead: { paddingHorizontal: 20, paddingTop: 14, paddingBottom: 4, letterSpacing: 2 },
  perkRow: { flexDirection: "row", alignItems: "flex-start", gap: 16, paddingVertical: 20, paddingHorizontal: 20, borderBottomWidth: 1, borderBottomColor: FL.hair },
  perkNum: { ...label(12), color: FL.muted, width: 30, paddingTop: 4, letterSpacing: 1.6 },
  perkTitle: { fontFamily: FONT, fontSize: 18, fontWeight: "700", color: FL.ink, letterSpacing: -0.2 },

  gate: { paddingBottom: 96 },
  gateInner: {
    ...glass({ strong: true, radius: 28 }),
    alignSelf: "center", width: "100%", maxWidth: 1480, overflow: "hidden",
    ...(WEB ? { backgroundImage: "radial-gradient(ellipse 70% 90% at 100% 100%, rgba(37,99,235,0.22) 0%, transparent 65%), radial-gradient(ellipse 60% 80% at 0% 0%, rgba(124,58,237,0.24) 0%, transparent 65%)" } : {}),
  },
  gateBody: { color: FL.stageSoft, marginTop: 24, maxWidth: 560 },
});
